import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { MessageModule } from 'primeng/message';
import { TranslateModule } from '@ngx-translate/core';
import { distinctUntilChanged, filter, Subscription } from 'rxjs';
import { PIN_PATTERN } from '../../crypto/key-backup-v5';
import { AuthService } from '../../service/auth.service';
import { V5_UPLOAD_MAX_BYTES } from '../../service/key-backup-v5.service';
import { KeyMigrationService } from '../../service/key-migration.service';
import {
    RsaMigrationInput, RsaMigrationPlace, RsaMigrationResult, RsaMigrationService
} from '../../service/rsa-migration.service';

export type RsaMigrationStage = 'hidden' | 'busy' | 'pin' | 'place' | 'secret' | 'file' | 'message';

const PREFIX = 'COMMON.RSA_MIGRATION.';
/** Kisa kosularda pencere hic gorunmesin diye bekleme gostergesi gecikmeli acilir. */
export const RSA_MIGRATION_SPINNER_DELAY_MS = 600;

/**
 * 14f: RSA -> PQ tasima penceresi (karar 2). Anahtar oturuma girince kendiliginden baslar;
 * "Basla" dugmesi ya da onay kutusu yoktur. Yalniz servis girdi isterse (yeni PIN, yer,
 * sunucu yedeginin PIN'i/kurtarma kodu, dosya kaniti) ya da kullanicinin bilmesi gereken bir
 * sonuc varsa gorunur. "Simdilik degil" kapatir; tasima sonraki giriste kaldigi yerden surer.
 * PIN/kod yalniz bellekte, pencere kapaninca silinir.
 */
@Component({
    selector: 'app-rsa-migration-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, DialogModule, ButtonModule, InputTextModule, RadioButtonModule, MessageModule, TranslateModule],
    templateUrl: './rsa-migration-dialog.component.html'
})
export class RsaMigrationDialogComponent implements OnInit, OnDestroy {
    stage: RsaMigrationStage = 'hidden';
    visible = false;
    /** Pencere kapandı (sessiz sonuç ya da kullanıcı). /tasi: anahtar oturumdaysa taşımaya devam eder. */
    @Output() readonly closed = new EventEmitter<void>();

    pin = '';
    pinConfirm = '';
    place: RsaMigrationPlace = 'server';
    secret = '';
    inlineKey: string | null = null;
    messageKey: string | null = null;
    messageSeverity: 'success' | 'info' = 'info';
    downloaded = false;
    purge = false;

    private blob: Uint8Array | null = null;
    /** Bu pencere oturumunda toplanan girdiler (needPin -> needPlace birlikte gonderilir). */
    private input: RsaMigrationInput = {};
    private spinnerTimer: ReturnType<typeof setTimeout> | null = null;
    /** Kullanici kapattiktan sonra donen sonuc pencereyi yeniden acmaz. */
    private dismissed = false;
    private sub: Subscription | null = null;

    private migration = inject(RsaMigrationService);
    private auth = inject(AuthService);
    private keyMigrationService = inject(KeyMigrationService);

    get busy(): boolean {
        return this.stage === 'busy';
    }

    ngOnInit(): void {
        // Anahtar oturuma girdiginde (giris ya da sonradan kilit acma) bir kez tetiklenir.
        this.sub = this.auth.privateKeyPresent$
            .pipe(distinctUntilChanged(), filter((present) => present))
            .subscribe(() => {
                if (this.stage === 'hidden') void this.start();
            });
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
        this.clearSpinner();
        this.wipe();
    }

    /** Otomatik kosu: girdi yok; sessiz sonuclar pencereyi hic gostermez. */
    async start(): Promise<void> {
        this.dismissed = false;
        this.input = {};
        await this.execute();
    }

    async submitPin(): Promise<void> {
        this.inlineKey = null;
        if (!PIN_PATTERN.test(this.pin)) {
            this.inlineKey = PREFIX + 'PIN_INVALID';
            return;
        }
        if (this.pin !== this.pinConfirm) {
            this.inlineKey = PREFIX + 'PIN_NOT_SAME';
            return;
        }
        this.input.pin = this.pin;
        await this.execute();
    }

    async submitPlace(): Promise<void> {
        this.inlineKey = null;
        this.input.place = this.place;
        await this.execute();
    }

    async submitSecret(): Promise<void> {
        this.inlineKey = null;
        const secret = this.secret.trim();
        if (!secret) return;
        this.input.secret = secret;
        await this.execute();
    }

    download(): void {
        if (!this.blob) return;
        this.keyMigrationService.downloadKeyFile(new Uint8Array(this.blob), fileName(new Date()));
        this.downloaded = true;
    }

    /** Kullanicinin kaydettigi dosyayi geri secmesi (FileProof). Istemci siniri: 1 MiB. */
    async onFile(event: Event): Promise<void> {
        this.inlineKey = null;
        const input = event.target as HTMLInputElement | null;
        const file = input?.files?.[0] ?? null;
        if (input) input.value = '';
        if (!file) return;
        if (file.size > V5_UPLOAD_MAX_BYTES) {
            this.inlineKey = PREFIX + 'FILE_TOO_LARGE';
            return;
        }
        let data: Uint8Array;
        try {
            data = new Uint8Array(await file.arrayBuffer());
        } catch (error) {
            console.warn('RsaMigrationDialogFileReadFailed', error instanceof Error ? error.message : 'unknown');
            this.inlineKey = PREFIX + 'FILE_UNREADABLE';
            return;
        }
        // Dosya tek kullanimliktir: servis bir kosuda tuketir, sonraki kosuya tasinmaz.
        await this.execute({ ...this.input, file: data });
    }

    digits(value: string): string {
        return (value ?? '').replace(/[^0-9]/g, '').slice(0, 6);
    }

    /** "Simdilik degil" / "Tamam" / X: kapanir; tasima kaydi kalir, sonraki giriste surer. */
    close(): void {
        this.dismissed = true;
        this.clearSpinner();
        this.stage = 'hidden';
        this.visible = false;
        this.wipe();
        this.closed.emit();
    }

    onVisibleChange(value: boolean): void {
        if (!value && this.visible) this.close();
    }

    private async execute(input: RsaMigrationInput = this.input): Promise<void> {
        const wasVisible = this.visible;
        this.stage = 'busy';
        this.clearSpinner();
        if (wasVisible) {
            this.visible = true;
        } else {
            this.spinnerTimer = setTimeout(() => {
                this.spinnerTimer = null;
                if (this.stage === 'busy' && !this.dismissed) this.visible = true;
            }, RSA_MIGRATION_SPINNER_DELAY_MS);
        }
        let result: RsaMigrationResult;
        try {
            result = await this.migration.run(input);
        } catch (error) {
            // Servis kendi hatalarini sonuca cevirir; buraya dusen beklenmedik durum loglanir.
            console.warn('RsaMigrationDialogFailed', error instanceof Error ? error.message : 'unknown');
            result = { kind: 'retryLater' };
        }
        this.clearSpinner();
        if (this.dismissed) return;
        this.show(result);
    }

    private show(result: RsaMigrationResult): void {
        switch (result.kind) {
            case 'disabled':
            case 'skipped':
                this.close();
                return;
            case 'done':
                if (result.fresh) this.message(result.locked > 0 ? 'DONE_LOCKED' : 'DONE', 'success');
                else this.close();
                return;
            case 'retryLater':
            case 'leaseHeld':
                // Kullanici bir sey girmediyse gecici durum sessiz kalir; girdiyse ne oldugunu gorur.
                if (this.visible) this.message(result.kind === 'leaseHeld' ? 'LEASE_HELD' : 'RETRY_LATER', 'info');
                else this.close();
                return;
            case 'needPin':
                this.prompt('pin');
                return;
            case 'needPlace':
                this.prompt('place');
                return;
            case 'needBackupSecret':
                this.prompt('secret');
                return;
            case 'wrongSecret':
                delete this.input.secret;
                this.secret = '';
                this.prompt('secret');
                this.inlineKey = PREFIX + 'SECRET_WRONG';
                return;
            case 'needFile':
            case 'fileMismatch':
                if (this.blob === null || !sameBytes(this.blob, result.blob)) this.downloaded = false;
                this.blob = result.blob;
                this.purge = result.purge;
                this.prompt('file');
                if (result.kind === 'fileMismatch') this.inlineKey = PREFIX + 'FILE_MISMATCH';
                return;
            case 'needsMobile':
                this.message('NEEDS_MOBILE', 'info');
                return;
            case 'lockedCards':
                this.message('LOCKED_CARDS', 'info');
                return;
            case 'keyMissing':
                this.message('KEY_MISSING', 'info');
                return;
        }
    }

    private prompt(stage: RsaMigrationStage): void {
        this.inlineKey = null;
        this.stage = stage;
        this.visible = true;
    }

    private message(key: string, severity: 'success' | 'info'): void {
        this.messageKey = PREFIX + key;
        this.messageSeverity = severity;
        this.stage = 'message';
        this.visible = true;
        // Sonuc gosterildi: girdiler bellekte tutulmaz.
        this.input = {};
        this.pin = '';
        this.pinConfirm = '';
        this.secret = '';
    }

    private clearSpinner(): void {
        if (this.spinnerTimer !== null) {
            clearTimeout(this.spinnerTimer);
            this.spinnerTimer = null;
        }
    }

    private wipe(): void {
        this.input = {};
        this.pin = '';
        this.pinConfirm = '';
        this.secret = '';
        this.inlineKey = null;
        this.messageKey = null;
        this.blob = null;
        this.downloaded = false;
        this.purge = false;
    }
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}

/** Yeni anahtar dosyasi adi: `kartex-<yyyy-mm-dd>.key` (yerel tarih). */
function fileName(now: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `kartex-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.key`;
}
